import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { isAbortError } from '../../services/ApiService';

const CacheWrapper = ({ 
  cacheKey, 
  cache, 
  setCache, 
  children, 
  fetchData,
  loading: initialLoading = false 
}) => {
  const [loading, setLoading] = useState(initialLoading);
  const [error, setError] = useState(null);
  const cachedData = cache[cacheKey];
  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;

  useEffect(() => {
    if (cachedData) {
      setLoading(false);
      setError(null);
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchDataRef.current({ signal: controller.signal });
        if (cancelled || controller.signal.aborted) return;
        setCache(prev => (
          prev[cacheKey] ? prev : { ...prev, [cacheKey]: data }
        ));
      } catch (err) {
        if (cancelled || controller.signal.aborted || isAbortError(err)) return;
        setError(err.message);
        console.error('Cache wrapper error:', err);
      } finally {
        if (!cancelled && !controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [cacheKey, cachedData, setCache]);

  if (loading && !cachedData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !cachedData) {
    return <div>Error: {error}</div>;
  }

  return children(cachedData);
};

export default CacheWrapper;
